import { useRef, useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import "swiper/css";
import "swiper/css/navigation";
import "../assets/styles/ProductSlider.css"; // Reuse styles if needed
import { Link } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

export default function ServiceSlider({ data = [], heading }) {
  // Added default empty array for data
  const swiperRef = useRef(null);
  const sliderContainerRef = useRef(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  useEffect(() => {
    // Ensure containerRef.current exists and has querySelectorAll method
    if (
      sliderContainerRef.current &&
      typeof sliderContainerRef.current.querySelectorAll === "function"
    ) {
      const cards =
        sliderContainerRef.current.querySelectorAll(".service-card-item"); // Use specific class

      if (cards.length > 0) {
        // Only run if cards exist
        gsap.fromTo(
          cards,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.2,
            scrollTrigger: {
              trigger: sliderContainerRef.current,
              start: "top 80%",
              toggleActions: "play none none reverse",
            },
          }
        );
      }
    }
  }, [data]); // Re-run if data changes

  // Handle case where data might be empty or not yet loaded
  if (!data || data.length === 0) {
    return (
      <div className="bg-gray-950 font-poppins py-10">
        <p className="text-white text-center">No services to display.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-950 font-poppins">
      <div
        ref={sliderContainerRef}
        className="relative mx-auto px-4 lg:px-0 pt-16 pb-24 product-card-container" // Check if this class is appropriate
      >
        {heading && (
          <h1 className="text-left text-4xl pb-10 font-medium text-white font-mono">
            {heading}
          </h1>
        )}
        <Swiper
          ref={swiperRef}
          modules={[Navigation]}
          spaceBetween={30}
          slidesPerView={4}
          breakpoints={{
            320: { slidesPerView: 1 },
            768: { slidesPerView: 3 },
            1024: { slidesPerView: 4 },
          }}
          navigation={{
            prevEl: ".custom-prev",
            nextEl: ".custom-next",
          }}
          onSlideChange={(swiper) => {
            setIsBeginning(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
          }}
          onInit={(swiper) => {
            setIsBeginning(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
          }}
          className="!overflow-visible"
        >
          {data.map((service, index) => (
            // Use _id if available, otherwise fallback to id or index
            <SwiperSlide
              key={service._id || service.id || index}
              className="service-card-item"
            >
              {/* Use _id for link */}
              <Link to={`/services/${service._id || service.id}`}>
                <div className="product-card group relative h-96 cursor-pointer overflow-hidden">
                  {" "}
                  {/* Check class name */}
                  {/* Card Content: Ensure all fields exist */}
                  <img
                    src={
                      service.imageUrl ||
                      "https://via.placeholder.com/400x300/CCCCCC/808080?text=Service"
                    } // Default image
                    alt={service.title || "Service"}
                    className="w-full h-full object-cover transform transition-all duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/40 transition-all duration-300 group-hover:bg-black/20" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white flex justify-center flex-col">
                    <h3 className="text-2xl font-bold mb-2 truncate-text">
                      {service.title || "No Title"}
                    </h3>
                    {/* Use default button text if not provided */}
                    <button className="opacity-100 group-hover:opacity-0 group-hover:-translate-y-4 transition-all duration-300 px-6 py-2 bg-[#cc673d] border-[#cc673d] text-white backdrop-blur-sm">
                      {service.buttonText || "More Details"}
                    </button>
                  </div>
                </div>
              </Link>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Navigation Arrows (Keep as is) */}
        <button
          className={`custom-prev ... ${
            isBeginning ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
        >
          ...
        </button>
        <button
          className={`custom-next ... ${
            isEnd ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
        >
          ...
        </button>
      </div>
    </div>
  );
}
